import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, isSupported, onMessage, type Messaging } from "firebase/messaging";
import Env from "@/core/config/env";

let messagingPromise: Promise<Messaging | null> | null = null;

async function getMessagingInstance(): Promise<Messaging | null> {
  if (typeof window === "undefined") return null;
  if (!(await isSupported())) return null;

  if (!messagingPromise) {
    messagingPromise = (async () => {
      if (!getApps().length) {
        initializeApp(Env.firebase);
      }
      return getMessaging();
    })();
  }

  return messagingPromise;
}

async function registerFcmServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  try {
    return await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  } catch {
    return null;
  }
}

export async function requestFcmPermission(): Promise<NotificationPermission> {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "denied";
  }

  return Notification.requestPermission();
}

export async function fetchFcmToken(): Promise<string | null> {
  const messaging = await getMessagingInstance();
  if (!messaging) return null;

  const registration = await registerFcmServiceWorker();

  try {
    return await getToken(messaging, {
      vapidKey: Env.fcmVapidKey || undefined,
      serviceWorkerRegistration: registration ?? undefined,
    });
  } catch {
    return null;
  }
}

export async function listenForForegroundMessages(handler: (payload: unknown) => void) {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => undefined;

  return onMessage(messaging, handler);
}
